import { useState, useEffect, useRef } from 'react';
import { createModel } from 'hox';
import { TablePaginationConfig } from 'antd/es/table/interface';
import { ISearchPage } from '../../../types/FmsTypes';
import { DEFAULT_FIRST_PAGE_NUM, DEFAULT_PAGE_SIZE } from '../../../utils/constants';
import { IPolicySearch, IPolicyDetail, IPolicyFields } from './policyTypes';
import { policyService } from './policyService';
import { transPgToPagination, urlParse, urlParse1 } from '../../../utils/utils';
import { UploadFile } from 'antd/lib/upload/interface';
import { IYhFormRef } from '@src/components/YhFormBox/Types';
import BraftEditor from 'braft-editor';
import { globalPrompt } from '@src/components/YhMessage';

export const usePolicyModel = createModel(function () {
	const initPgCfg: TablePaginationConfig = {
		pageSize: DEFAULT_PAGE_SIZE,
		current: DEFAULT_FIRST_PAGE_NUM,
	};
	const initSearchParam: IPolicyFields = {};
	const initDataSource: IPolicyDetail[] = [];

	const formRef = useRef<IYhFormRef>();

	// 查询条件
	const [searchParam, setSearchParam] = useState<IPolicyFields>(initSearchParam);
	// 列表数据源
	const [dataSource, setDataSource] = useState<IPolicyDetail[]>(initDataSource);
	// 表格选中行key值
	const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
	const [selectedRowData, setSelectedRowData] = useState<IPolicyDetail>();
	// 表格选中的值
	const [selectedRow, setSelectRow] = useState<IPolicyDetail>({});
	// 列表数据源分页参数
	const [pgCfg, setPgCfg] = useState<TablePaginationConfig>(initPgCfg);
	const [loading, setLoading] = useState<boolean>(false);

	// 富文本  新增
	const [addEditorState, setAddEditorState] = useState<string>(''); //富文本
	// 富文本 编辑
	const [editEditorState, setEditEditorState] = useState<string>(''); //富文本
	const [previewImage, setPreviewImage] = useState(Boolean); //商品缩略图预览
	// 上传图片的地址
	const [imageUrl, setImageUrl] = useState('');
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [previewUrl, setPreviewUrl] = useState('');

	// 编辑缓存
	const [editFormVal, setEditFormVal] = useState<IPolicyFields>({});
	const [editGoodsList, setEditGoodsList] = useState<IPolicyFields[]>([]);
	// 新增缓存
	const [addFormVal, setAddFormVal] = useState<IPolicyFields>({});
	const [addGoodsList, setAddGoodsList] = useState<IPolicyFields[]>([]);

	useEffect(() => {
		let filters = dataSource.filter((el) =>
			selectedRowKeys.length > 0 ? el.id === selectedRowKeys[0] : false
		);
		const selectedData = filters && filters.length > 0 ? filters[0] : undefined;
		setSelectRow(selectedData as IPolicyFields);
	}, [setSelectedRowKeys]);

	// 更新页码
	const updataPgCfg = (updataParam: TablePaginationConfig) => {
		setPgCfg({ ...pgCfg, ...updataParam });
	};

	// 更新搜索条件
	const updataSearchParam = (updataParam: IPolicySearch) => {
		setSearchParam({ ...searchParam, ...updataParam });
	};

	// 重置搜索条件
	const resetSearchParam = () => {
		setSearchParam(initSearchParam);
		loadData(initPgCfg, {});
		setSelectedRowKeys([]);
		setSelectRow({});
	};

	// 数据加载
	const loadData = async (page?: ISearchPage, searchValue: IPolicyDetail = searchParam) => {
		const searchParams: IPolicySearch = {
			...searchValue,
			insertType: 'policy',
			pg: {
				orders: [
					{
						rule: 'ASC',
						column: 'createTime',
					},
				],
				pageSize: page?.pageSize ?? pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
				pageNum: (page?.pageNum || page?.current) ?? pgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
			},
		};
		let { retData } = await policyService.page(searchParams);
		const { items, ...pg } = retData;
		setPgCfg(transPgToPagination(pg));
		setDataSource(items);
	};

	// 删除
	const onRemove = async (id: string) => {
		try {
			const { retData } = await policyService.remove(id);
			if (retData) {
				globalPrompt('message', { text: '删除成功', type: 'success' });
				loadData();
			} else {
				globalPrompt('message', { text: '删除失败', type: 'error' });
			}
		} catch (error) {
			console.log(error);
		}
	};

	//表单新增修改
	const updateAddFormVal = (val: object | null) => {
		val ? setAddFormVal({ ...addFormVal, ...val }) : setAddFormVal({});
	};
	//表单编辑修改
	const updateEditFormVal = (val: object | null) => {
		val ? setEditFormVal({ ...editFormVal, ...val }) : setEditFormVal({});
	};

	// 新增api
	const insertDetail = async (params: IPolicyFields) => {
		let newParams = {
			...params,
		};
		let { retData } = await policyService.insert(newParams);
		return retData;
	};

	//编辑api
	const editDetail = async (params: IPolicyFields) => {
		let newParams = {
			...params,
		};
		let { retData } = await policyService.update(newParams);
		return retData;
	};
	// 获取一条数据
	const getDetailEditOne = async (id: string, mode: string) => {
		let { retData } = await policyService.one(id);
		const { ...form } = retData;
		if (retData) {
			if (mode === 'edit') {
				setEditFormVal(form);
				setEditEditorState(BraftEditor.createEditorState(form.articl));
			} else {
				setAddFormVal(form);
				setAddEditorState(BraftEditor.createEditorState(form.articl));
			}
		} else {
			globalPrompt('message', { type: 'success', text: '操作失败请重试!' });
		}
	};

	// 翻译
	const translate = async (params: IPolicyFields, id: string, mode: string) => {
		// debugger;
		let isEdit = mode === 'edit';
		let formVal = isEdit ? editFormVal : addFormVal;
		let editorState = isEdit ? editEditorState : addEditorState ?? '';
		let arrIndex: string[] = [];
		let arr: string[] = editorState.match(/(<[a-zA-Z]+.*?>|<\/[a-zA-Z]+.*?>|[^><]+)/g) ?? [];
		let str: string = '';
		arr.forEach((item, index) => {
			if (item.indexOf('<') < 0 && item.indexOf('>') < 0) {
				arrIndex.push(`${index}`);
				str += `&${index}=${arr[index]}`;
			}
		});
		console.log(arrIndex, arr);
		let translationObj = {
			source: formVal.source,
			keyword: formVal.keyword,
			title: formVal.title,
			desc: formVal.desc,
			createUser: formVal.createUser,
			article: str,
		};
		let retData = await policyService.translate(translationObj);
		console.log('返回数据', retData);
		let obj: any = urlParse1(retData.retData?.article ?? '');
		console.log(obj);
		(isEdit ? setEditFormVal : setAddFormVal)({
			source: retData.retData.source,
			keyword: retData.retData.keyword,
			title: retData.retData.title?.trim(),
			desc: retData.retData.desc,
			createUser: retData.retData.createUser,
			createTime: editFormVal?.createTime,
			author: editFormVal?.author,
			id: editFormVal?.id,
		});
		arrIndex.forEach((item) => {
			arr[parseInt(item)] = obj[item];
		});
		(isEdit ? setEditEditorState : setAddEditorState)(BraftEditor.createEditorState(arr.join('')));
	};

	//重置缓存
	const resetEditCatch = (type?: string) => {
		updateEditFormVal(null); //表单
		setEditFormVal({});
	};
	const resetAddCatch = (type?: string) => {
		updateAddFormVal(null);
		setAddFormVal({});
	};
	return {
		formRef,
		initPgCfg,
		searchParam,
		dataSource,
		selectedRowKeys,
		selectedRow,
		pgCfg,
		loading,
		setSearchParam,
		setDataSource,
		setSelectedRowKeys,
		setSelectRow,
		setPgCfg,
		setLoading,
		updataPgCfg,
		updataSearchParam,
		resetSearchParam,
		loadData,
		onRemove,
		imageUrl,
		setImageUrl,
		fileList,
		setFileList,
		addEditorState,
		setAddEditorState,
		editEditorState,
		setEditEditorState,
		previewImage,
		setPreviewImage,
		previewUrl,
		setPreviewUrl,
		updateAddFormVal,
		updateEditFormVal,
		resetEditCatch,
		resetAddCatch,
		editFormVal,
		setEditFormVal,
		editGoodsList,
		setEditGoodsList,
		addFormVal,
		setAddFormVal,
		addGoodsList,
		setAddGoodsList,
		getDetailEditOne,
		editDetail,
		insertDetail,
		translate,
		selectedRowData,
		setSelectedRowData,
	};
});
